import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player/lazy';
import { Alert } from '@ver-uds/uswds-react';
import { User } from '@vis-auth/vis-user-client';
import I9Constants from '../I9Constants/I9Constants';
import VideoUnavailableSmall from '../../images/VideoUnavailable/VideoUnavailableSmall.png';
import './I9TrainingVideo.scss';
import { addNewRelicCustomAttribute } from '../../utils/new-relic-help';
import { useUser } from '../../context/User/UserContext';

interface Props {
  className?: string;
  videoRef?: React.RefObject<ReactPlayer>;
  backupVideoRef?: React.RefObject<ReactPlayer>;
  videoUnavailableSrc?: string;
  handleVideoEnded?: () => void;
  handleProgress?: (activeVideoRef: React.RefObject<ReactPlayer> | undefined) => void;
  forceBackupVideo?: boolean;
}

const I9TrainingVideo = ({
  className,
  videoRef,
  backupVideoRef,
  videoUnavailableSrc,
  handleVideoEnded,
  handleProgress,
  forceBackupVideo,
}: Props): JSX.Element => {
  const { accountId, logonId } = useUser() as User;
  const [youTubeVideoError, setYouTubeVideoError] = useState<boolean>(false);
  const [showVideoError, setShowVideoError] = useState<boolean>(false);

  const handleYouTubeVideoError = (): void => {
    setYouTubeVideoError(true);
    addNewRelicCustomAttribute('YOUTUBE_ERROR_USER_ID', logonId);
    addNewRelicCustomAttribute('YOUTUBE_ERROR_ACCOUNT_ID', accountId);
  };

  const handleBothVideosFailError = (): void => {
    setShowVideoError(true);
    addNewRelicCustomAttribute('VIDEO_ERROR_USER_ID', logonId);
    addNewRelicCustomAttribute('VIDEO_ERROR_ACCOUNT_ID', accountId);
  };

  useEffect(() => {
    if (logonId) {
      addNewRelicCustomAttribute('VIEWING_TRAINING_USER_ID', logonId);
    }
  }, [logonId]);

  return showVideoError ? (
    <>
      <img className="margin-0" src={videoUnavailableSrc} alt="Video Unavailable" />
      <Alert status="error" slim className="margin-top-1">
        <Alert.Text>
          Having trouble viewing the {I9Constants.PROGRAM_NAME} training video? Email support at{' '}
          <a
            aria-label="Click here to send an email to E-VerifyPlus@uscis.dhs.gov"
            href="mailto:E-VerifyPlus@uscis.dhs.gov"
            rel="noopener noreferrer"
            target="_blank"
            className="nobr"
          >
            E-VerifyPlus@uscis.dhs.gov
          </a>
          .
        </Alert.Text>
      </Alert>
    </>
  ) : (
    <div className="training-video-container">
      {!youTubeVideoError && !forceBackupVideo && (
        <>
          <ReactPlayer
            url={`https://www.youtube.com/watch?v=${I9Constants.TRAINING_VIDEO_ID}`}
            loop={false}
            controls
            onEnded={handleVideoEnded}
            className={`training-video ${className}`}
            onProgress={() => (handleProgress ? handleProgress(videoRef) : null)}
            ref={videoRef}
            width="100%"
            height="100%"
            onError={handleYouTubeVideoError}
          />
        </>
      )}
      <div className={forceBackupVideo ? 'display-block' : 'display-none'}>
        <ReactPlayer
          data-testid="video"
          url={`${process.env.PUBLIC_URL}/videos/everify-plus-training.mp4`}
          loop={false}
          controls
          onEnded={handleVideoEnded}
          className={`training-video backup-training-video ${className}`}
          onProgress={() => (handleProgress ? handleProgress(backupVideoRef) : null)}
          ref={backupVideoRef}
          width="100%"
          height="100%"
          onError={handleBothVideosFailError}
          config={{
            file: {
              tracks: [
                {
                  label: 'label',
                  kind: 'subtitles',
                  src: `${process.env.PUBLIC_URL}/videos/E_Verify_Demo_v9.vtt`,
                  srcLang: 'en',
                  default: true,
                },
              ],
            },
          }}
        />
      </div>
    </div>
  );
};

I9TrainingVideo.defaultProps = {
  className: '',
  videoRef: undefined,
  videoUnavailableSrc: VideoUnavailableSmall,
  handleVideoEnded: undefined,
  backupVideoRef: undefined,
  handleProgress: null,
  forceBackupVideo: false,
};

export default I9TrainingVideo;
